import React, { useState } from 'react';
import { ListaCotizacionesServiciosCV } from './listaCotizacionesServiciosCV';
import { CrearCotizacionServiciosCV } from './crearCotizacionServiciosCV';
import { generarIdTemporal } from '../../configuracionCotizadorServicios/interfaces';
import type { ICotizacionServicios, ICotizacionServiciosResumen } from '../../../modelos/CotizadorServicios/entidades/ICotizacionServicios';
import { useNotificaciones } from '../../../globales/contextos/notificationContext';
import { EnumTipoNotificacion } from '../../../globales/interfaces';

const sumarCantidades = (equipos: { Cantidad: number }[]) => equipos.reduce((total, e) => total + e.Cantidad, 0);

/** Fila del listado a partir de una cotización completa. */
const resumirCotizacion = (c: ICotizacionServicios): ICotizacionServiciosResumen => ({
    Id: c.Id as string,
    Proyecto: c.Proyecto,
    Cliente: c.Cliente,
    Anio: c.Anio,
    TotalAscensores: sumarCantidades(c.Ascensores),
    TotalEscaleras: sumarCantidades(c.Escaleras),
    TotalPlanConekta: (c.Resultado?.Ascensores.Totales.SinIva.PlanConekta.Total ?? 0) + (c.Resultado?.Escaleras.Totales.SinIva.PlanConekta.Total ?? 0),
    TotalPlanConektaPlus: (c.Resultado?.Ascensores.Totales.SinIva.PlanConektaPlus.Total ?? 0) + (c.Resultado?.Escaleras.Totales.SinIva.PlanConektaPlus.Total ?? 0),
    Estado: c.Estado,
    FechaCreacion: c.FechaCreacion ?? new Date().toISOString(),
    UsuarioCreadorNombre: c.UsuarioCreadorNombre
});

/** Alterna entre el listado y el formulario de la cotización. */
export const CotizadorServiciosCV: React.FC = () => {
    const { mostrarNotificacion } = useNotificaciones();
    const [vistaActiva, setVistaActiva] = useState<'lista' | 'crear'>('lista');
    const [cotizacionAEditar, setCotizacionAEditar] = useState<ICotizacionServicios | undefined>();
    // TODO back: listar, consultar, guardar y eliminar en el servicio del cotizador. Mientras tanto, en memoria.
    const [cotizaciones, setCotizaciones] = useState<ICotizacionServicios[]>([]);

    const abrirFormulario = (cotizacion?: ICotizacionServicios) => {
        setCotizacionAEditar(cotizacion);
        setVistaActiva('crear');
    };

    const volverAlListado = () => {
        setCotizacionAEditar(undefined);
        setVistaActiva('lista');
    };

    const guardar = (cotizacion: ICotizacionServicios) => {
        const guardada = { ...cotizacion, Id: cotizacion.Id ?? generarIdTemporal() };
        setCotizaciones(prev => prev.some(c => c.Id === guardada.Id)
            ? prev.map(c => c.Id === guardada.Id ? guardada : c)
            : [guardada, ...prev]);
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Cotización guardada', `La cotización de ${guardada.Proyecto} quedó guardada.`, 3000);
        volverAlListado();
    };

    if (vistaActiva === 'crear') {
        return <CrearCotizacionServiciosCV cotizacionInicial={cotizacionAEditar} alGuardar={guardar} alVolver={volverAlListado} />;
    }

    return (
        <ListaCotizacionesServiciosCV
            cotizaciones={cotizaciones.map(resumirCotizacion)}
            alCrearNueva={() => abrirFormulario()}
            alEditar={(id) => abrirFormulario(cotizaciones.find(c => c.Id === id))}
            alEliminar={(id) => setCotizaciones(prev => prev.filter(c => c.Id !== id))}
            alRefrescar={() => { /* TODO back: volver a consultar el listado. */ }}
        />
    );
};
